import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import TextField from '@mui/material/TextField'
import Checkbox from '@mui/material/Checkbox'
import IconButton from '@mui/material/IconButton'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { RESIZABLE_DIALOG_PAPER } from '@/shared/resizableDialog'
import type { ImportDefault, EnumResolveEntry } from '@/shared/import'
import { ApiService } from '@/utils/api'
import { confirmAsync } from '@/shared/dialogs'
import type { TabularColumn } from './TabularGrid'
import { parseTabularImportFile, resolveRefsBatch, resolveEnumsBatch } from './tabularImportApi'
import {
  heuristicTabularMapping,
  buildTabularImportRows,
  mergeTabularImport,
  assignLineNumbers,
  type TabularMapTarget,
  type TabularImportWarning,
  type TabularColumnMeta,
  type ResolveMapEntry,
} from './tabularImport'

export interface TabularImportDialogProps {
  open: boolean
  onClose: () => void
  connectionId: string
  objectName: string
  columns: TabularColumn[]
  existingRows: Record<string, unknown>[]
  onApply: (nextRows: Record<string, unknown>[]) => void
}

const STEPS = ['Upload', 'Map', 'Preview']

/**
 * Resolve a ref/enum column's target object name. The runtime emits the target under
 * `props.target` (mirrors TabularGrid.refTarget); the top-level `attr.target`
 * is kept only as a legacy fallback.
 */
function refTargetOf(attr: TabularColumn['attr'] | undefined): string | undefined {
  const props = (attr?.props ?? {}) as Record<string, unknown>
  const t = props.target ?? props.referenceTarget ?? props.reference_target ?? attr?.target
  return typeof t === 'string' && t ? t : undefined
}

function attrTypeOf(attr: TabularColumn['attr'] | undefined): string {
  return String(attr?.attr_type || 'string').toLowerCase()
}

function compositeTypesOf(attr: TabularColumn['attr'] | undefined): string[] {
  const raw = attr?.props?.composite_types
  if (!Array.isArray(raw)) return []
  return raw.map(String).filter(Boolean)
}

function makeDefaultForColumn(col: TabularColumn): ImportDefault {
  const t = attrTypeOf(col.attr)
  if (t === 'boolean') {
    return { attr: col.name, kind: 'scalar', value: true }
  }
  if (t === 'enum_ref' || t.includes('enum')) {
    return { attr: col.name, kind: 'enum', value: '' }
  }
  if (t === 'ref' || t === 'reference') {
    return { attr: col.name, kind: 'ref', lookupField: 'name', text: '' }
  }
  if (t === 'composite_ref') {
    const types = compositeTypesOf(col.attr)
    return {
      attr: col.name,
      kind: 'composite',
      type: types[0] || '',
      value: '',
    }
  }
  if (t === 'number' || t === 'integer' || t === 'decimal') {
    return { attr: col.name, kind: 'scalar', value: 0 }
  }
  return { attr: col.name, kind: 'scalar', value: '' }
}

function remapDefaultToColumn(prev: ImportDefault, col: TabularColumn): ImportDefault {
  const next = makeDefaultForColumn(col)
  // Preserve typed value when kind stays compatible.
  if (prev.kind === 'scalar' && next.kind === 'scalar') {
    return { ...next, value: prev.value }
  }
  if (prev.kind === 'enum' && next.kind === 'enum') {
    return { ...next, value: prev.value }
  }
  if (prev.kind === 'ref' && next.kind === 'ref') {
    return { ...next, lookupField: prev.lookupField, text: prev.text }
  }
  if (prev.kind === 'composite' && next.kind === 'composite') {
    return { ...next, value: prev.value, type: prev.type || next.type }
  }
  return next
}

export function TabularImportDialog({
  open,
  onClose,
  connectionId,
  objectName,
  columns,
  existingRows,
  onApply,
}: TabularImportDialogProps) {
  const [step, setStep] = useState(0)
  const [file, setFile] = useState<File | null>(null)
  const [fileColumns, setFileColumns] = useState<string[]>([])
  const [fileRows, setFileRows] = useState<Record<string, unknown>[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [mapping, setMapping] = useState<Record<string, TabularMapTarget>>({})
  const [mode, setMode] = useState<'append' | 'replace'>('append')
  const [defaults, setDefaults] = useState<ImportDefault[]>([])
  const [processedRows, setProcessedRows] = useState<Record<string, unknown>[]>([])
  const [warnings, setWarnings] = useState<TabularImportWarning[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lookupFieldsCache, setLookupFieldsCache] = useState<Record<string, string[]>>({})
  const [enumOptionsCache, setEnumOptionsCache] = useState<Record<string, string[]>>({})
  const loadedTargetsRef = useRef<Set<string>>(new Set())
  const loadedEnumTargetsRef = useRef<Set<string>>(new Set())

  const reset = () => {
    setStep(0)
    setFile(null)
    setFileColumns([])
    setFileRows([])
    setTotalRows(0)
    setMapping({})
    setMode('append')
    setDefaults([])
    setProcessedRows([])
    setWarnings([])
    setError(null)
    setLookupFieldsCache({})
    setEnumOptionsCache({})
    loadedTargetsRef.current.clear()
    loadedEnumTargetsRef.current.clear()
  }

  const columnMeta = useMemo((): TabularColumnMeta[] => {
    return columns.map((col) => ({
      name: col.name,
      attr_type: col.attr.attr_type || 'string',
      target: refTargetOf(col.attr),
    }))
  }, [columns])

  const attrTypes = useMemo(() => {
    const result: Record<string, string> = {}
    columns.forEach((col) => {
      result[col.name] = col.attr.attr_type || 'string'
    })
    return result
  }, [columns])

  const columnsByName = useMemo(() => {
    const m = new Map<string, TabularColumn>()
    columns.forEach((c) => m.set(c.name, c))
    return m
  }, [columns])

  const usedDefaultAttrs = useMemo(
    () => new Set(defaults.map((d) => d.attr)),
    [defaults],
  )

  // Load lookup fields for ref columns
  const loadLookupFields = useCallback(async (target: string) => {
    if (lookupFieldsCache[target]) return lookupFieldsCache[target]

    try {
      const fieldsResp = await ApiService.get<{ fields: { name: string; type: string }[] }>(
        `/v1/import/${connectionId}/${target}/fields`,
      )
      const stringFields = fieldsResp.fields
        .filter((f) => ['string', 'text'].includes(f.type.toLowerCase()))
        .map((f) => f.name)
      setLookupFieldsCache((prev) => ({ ...prev, [target]: stringFields }))
      return stringFields
    } catch (err) {
      console.warn(`Failed to load lookup fields for ${target}:`, err)
      setLookupFieldsCache((prev) => ({ ...prev, [target]: [] }))
      return []
    }
  }, [connectionId])

  const loadEnumOptions = useCallback(async (enumTarget: string) => {
    if (enumOptionsCache[enumTarget]) return enumOptionsCache[enumTarget]
    try {
      const res = await ApiService.get<unknown>(
        `/v1/app/${encodeURIComponent(connectionId)}/${encodeURIComponent(enumTarget)}/lookup`,
      )
      const nested = (res as { data?: { id?: string }[] })?.data
      const rows = Array.isArray(res)
        ? (res as { id?: string }[])
        : Array.isArray(nested)
          ? nested
          : []
      const names = rows
        .map((r) => (typeof r?.id === 'string' ? r.id : ''))
        .filter(Boolean)
      setEnumOptionsCache((prev) => ({ ...prev, [enumTarget]: names }))
      return names
    } catch (err) {
      console.warn(`Failed to load enum options for ${enumTarget}:`, err)
      setEnumOptionsCache((prev) => ({ ...prev, [enumTarget]: [] }))
      return []
    }
  }, [connectionId])

  const handleFileSelect = useCallback(
    async (selectedFile: File) => {
      setFile(selectedFile)
      setError(null)
      setLoading(true)

      try {
        const result = await parseTabularImportFile(connectionId, objectName, selectedFile)
        setFileColumns(result.columns)
        setFileRows(result.rows)
        setTotalRows(result.total_rows)

        const heuristic = heuristicTabularMapping(result.columns, columnMeta)
        setMapping(heuristic)

        setStep(1)
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Failed to parse file'
        setError(msg)
      }
      setLoading(false)
    },
    [connectionId, objectName, columnMeta],
  )

  // Reset when dialog opens/closes
  useEffect(() => {
    if (open) reset()
  }, [open])

  // Load lookup fields for ref columns when mapping changes
  useEffect(() => {
    const processedInThisRun = new Set<string>()

    Object.entries(mapping).forEach(([col, target]) => {
      if (target.kind === 'attr' && target.name) {
        const column = columns.find((c) => c.name === target.name)
        const colTarget = refTargetOf(column?.attr)
        if (column && attrTypeOf(column.attr) === 'ref') {
          if (colTarget && !processedInThisRun.has(colTarget)) {
            processedInThisRun.add(colTarget)
            loadLookupFields(colTarget)
            setMapping((prev) => ({
              ...prev,
              [col]: {
                kind: 'ref',
                attr: target.name,
                lookupField: 'name',
              },
            }))
          }
        }
      } else if (target.kind === 'ref' && target.attr) {
        const column = columns.find((c) => c.name === target.attr)
        const colTarget = refTargetOf(column?.attr)
        if (
          colTarget &&
          !processedInThisRun.has(colTarget) &&
          !loadedTargetsRef.current.has(colTarget)
        ) {
          processedInThisRun.add(colTarget)
          loadedTargetsRef.current.add(colTarget)
          loadLookupFields(colTarget)
        }
      }
    })
  }, [mapping, columns, loadLookupFields])

  // Lazy-load enum options + ref lookup fields for defaults editors
  useEffect(() => {
    defaults.forEach((d) => {
      const col = columnsByName.get(d.attr)
      if (!col) return
      if (d.kind === 'enum') {
        const enumTarget = refTargetOf(col.attr)
        if (enumTarget && !loadedEnumTargetsRef.current.has(enumTarget)) {
          loadedEnumTargetsRef.current.add(enumTarget)
          loadEnumOptions(enumTarget)
        }
      } else if (d.kind === 'ref') {
        const refTarget = refTargetOf(col.attr)
        if (refTarget && !loadedTargetsRef.current.has(refTarget)) {
          loadedTargetsRef.current.add(refTarget)
          loadLookupFields(refTarget)
        }
      }
    })
  }, [defaults, columnsByName, loadEnumOptions, loadLookupFields])

  const handleMappingChange = useCallback((column: string, target: TabularMapTarget) => {
    setMapping((prev) => ({ ...prev, [column]: target }))
  }, [])

  const mappedAttrNames = useMemo(() => {
    const names = new Set<string>()
    Object.values(mapping).forEach((target) => {
      if (target.kind === 'attr') names.add(target.name)
      if (target.kind === 'ref') names.add(target.attr)
    })
    return names
  }, [mapping])

  const handleAddDefault = useCallback(() => {
    // Prefer attrs not already mapped from the file (typical default use-case).
    const nextCol =
      columns.find((c) => !usedDefaultAttrs.has(c.name) && !mappedAttrNames.has(c.name)) ||
      columns.find((c) => !usedDefaultAttrs.has(c.name))
    if (!nextCol) return
    setDefaults((prev) => [...prev, makeDefaultForColumn(nextCol)])
  }, [columns, usedDefaultAttrs, mappedAttrNames])

  const handleDefaultAttrChange = useCallback(
    (index: number, attrName: string) => {
      const col = columnsByName.get(attrName)
      if (!col) return
      setDefaults((prev) =>
        prev.map((d, i) => (i === index ? remapDefaultToColumn(d, col) : d)),
      )
    },
    [columnsByName],
  )

  const handleDefaultPatch = useCallback((index: number, patch: ImportDefault) => {
    setDefaults((prev) => prev.map((d, i) => (i === index ? patch : d)))
  }, [])

  const handleRemoveDefault = useCallback((index: number) => {
    setDefaults((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const buildPreview = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Collect ref mappings for batch resolution (columns + ref defaults)
      const refMappings = new Map<string, { attr: string; lookupField: string }>()
      const distinctValues = new Map<string, Set<string>>()

      const addRefValues = (attr: string, lookupField: string, values: string[]) => {
        const mapKey = `${attr}|${lookupField}`
        if (!refMappings.has(mapKey)) {
          refMappings.set(mapKey, { attr, lookupField })
        }
        const set = distinctValues.get(mapKey) ?? new Set<string>()
        values.forEach((v) => {
          if (v) set.add(v)
        })
        distinctValues.set(mapKey, set)
      }

      for (const [column, target] of Object.entries(mapping)) {
        if (target.kind === 'ref') {
          const values: string[] = []
          fileRows.forEach((row) => {
            const value = row[column]
            if (value != null && String(value).trim()) {
              values.push(String(value).trim())
            }
          })
          addRefValues(target.attr, target.lookupField, values)
        }
      }

      for (const d of defaults) {
        if (d.kind === 'ref' && d.text.trim()) {
          addRefValues(d.attr, d.lookupField, [d.text.trim()])
        }
      }

      const resolveMaps: Record<string, Record<string, ResolveMapEntry>> = {}
      const resolvePromises = Array.from(refMappings.entries()).map(
        async ([mapKey, { attr, lookupField }]) => {
          const values = Array.from(distinctValues.get(mapKey) || [])
          if (values.length === 0) {
            resolveMaps[mapKey] = {}
            return
          }

          const refCol = columns.find((c) => c.name === attr)
          const refColTarget = refTargetOf(refCol?.attr)
          if (!refColTarget) {
            resolveMaps[mapKey] = {}
            return
          }

          try {
            const resolved = await resolveRefsBatch(connectionId, {
              target: refColTarget,
              field: lookupField,
              values,
            })
            resolveMaps[mapKey] = resolved
          } catch {
            resolveMaps[mapKey] = {}
          }
        },
      )

      await Promise.all(resolvePromises)

      // Enum attrs from mapping + enum defaults
      const enumAttrSet = new Set<string>()
      const valuesByEnumTarget = new Map<string, Set<string>>()
      const attrsByEnumTarget = new Map<string, Set<string>>()

      const registerEnum = (attrName: string, rawValues: string[]) => {
        const col = columnsByName.get(attrName)
        if (!col || attrTypeOf(col.attr) !== 'enum_ref') return
        const enumTarget = refTargetOf(col.attr)
        if (!enumTarget) return
        enumAttrSet.add(attrName)
        const attrSet = attrsByEnumTarget.get(enumTarget) ?? new Set<string>()
        attrSet.add(attrName)
        attrsByEnumTarget.set(enumTarget, attrSet)
        const valueSet = valuesByEnumTarget.get(enumTarget) ?? new Set<string>()
        rawValues.forEach((v) => {
          if (v) valueSet.add(v)
        })
        valuesByEnumTarget.set(enumTarget, valueSet)
      }

      for (const [column, target] of Object.entries(mapping)) {
        if (target.kind !== 'attr' || !target.name) continue
        const values: string[] = []
        fileRows.forEach((row) => {
          const value = row[column]
          if (value != null && String(value).trim()) {
            values.push(String(value).trim())
          }
        })
        registerEnum(target.name, values)
      }

      for (const d of defaults) {
        if (d.kind === 'enum' && d.value.trim()) {
          registerEnum(d.attr, [d.value.trim()])
        }
      }

      const enumAttrs = Array.from(enumAttrSet)
      const enumMaps: Record<string, Record<string, EnumResolveEntry>> = {}
      let enumResolveFailed = false

      const enumPromises = Array.from(attrsByEnumTarget.entries()).map(
        async ([enumTarget, attrs]) => {
          const values = Array.from(valuesByEnumTarget.get(enumTarget) || [])
          let resolved: Record<string, EnumResolveEntry> = {}
          if (values.length > 0) {
            try {
              resolved = await resolveEnumsBatch(connectionId, {
                enum_target: enumTarget,
                object_name: objectName,
                values,
              })
            } catch {
              enumResolveFailed = true
              resolved = {}
            }
          }
          attrs.forEach((attr) => {
            enumMaps[attr] = resolved
          })
        },
      )

      await Promise.all(enumPromises)

      if (enumResolveFailed) {
        setError('Enum resolve unavailable — enum cells left unresolved where possible.')
      }

      const { rows, warnings: buildWarnings } = buildTabularImportRows({
        fileRows,
        mapping,
        resolveMaps,
        attrTypes,
        mapColumnOrder: fileColumns,
        enumAttrs,
        enumMaps,
        defaults,
      })

      setProcessedRows(rows)
      setWarnings(buildWarnings)
      setStep(2)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to build preview'
      setError(msg)
    }
    setLoading(false)
  }, [
    fileRows,
    mapping,
    fileColumns,
    columns,
    columnsByName,
    connectionId,
    objectName,
    attrTypes,
    defaults,
  ])

  const handleConfirm = useCallback(async () => {
    if (mode === 'replace' && existingRows.length > 0) {
      if (!(await confirmAsync('Replace all existing rows in this table with the imported rows?'))) {
        return
      }
    }

    const withLineNumbers = assignLineNumbers(
      processedRows,
      mode === 'append' ? existingRows.length + 1 : 1,
    )

    const merged = mergeTabularImport(existingRows, withLineNumbers, mode)
    onApply(merged)
    onClose()
  }, [mode, existingRows, processedRows, onApply, onClose])

  const mappedCount = Object.values(mapping).filter((target) => target.kind !== 'skip').length
  const canPreview = mappedCount > 0 && fileRows.length > 0
  const canAddDefault = columns.some((c) => !usedDefaultAttrs.has(c.name))

  const previewColumns = useMemo(() => {
    const mapped = new Set<string>()
    Object.values(mapping).forEach((target) => {
      if (target.kind === 'attr') mapped.add(target.name)
      if (target.kind === 'ref') mapped.add(target.attr)
    })
    defaults.forEach((d) => mapped.add(d.attr))
    return columns.filter((col) => mapped.has(col.name))
  }, [columns, mapping, defaults])

  const renderDefaultEditor = (def: ImportDefault, index: number) => {
    const col = columnsByName.get(def.attr)
    const title = col?.title || def.attr

    if (def.kind === 'scalar') {
      const t = attrTypeOf(col?.attr)
      if (t === 'boolean') {
        return (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={Boolean(def.value)}
                onChange={(e) =>
                  handleDefaultPatch(index, {
                    attr: def.attr,
                    kind: 'scalar',
                    value: e.target.checked,
                  })
                }
                inputProps={{ 'aria-label': `${title} default` }}
              />
            }
            label={title}
          />
        )
      }
      if (t === 'number' || t === 'integer' || t === 'decimal') {
        return (
          <TextField
            size="small"
            type="number"
            label="Value"
            value={def.value ?? ''}
            onChange={(e) => {
              const raw = e.target.value
              const num = raw === '' ? null : Number(raw)
              handleDefaultPatch(index, {
                attr: def.attr,
                kind: 'scalar',
                value: Number.isFinite(num as number) ? (num as number) : raw,
              })
            }}
            fullWidth
          />
        )
      }
      if (t === 'date' || t === 'datetime') {
        return (
          <TextField
            size="small"
            type={t === 'datetime' ? 'datetime-local' : 'date'}
            label="Value"
            value={typeof def.value === 'string' ? def.value : ''}
            onChange={(e) =>
              handleDefaultPatch(index, {
                attr: def.attr,
                kind: 'scalar',
                value: e.target.value,
              })
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        )
      }
      return (
        <TextField
          size="small"
          label="Value"
          value={def.value ?? ''}
          onChange={(e) =>
            handleDefaultPatch(index, {
              attr: def.attr,
              kind: 'scalar',
              value: e.target.value,
            })
          }
          fullWidth
        />
      )
    }

    if (def.kind === 'enum') {
      const enumTarget = refTargetOf(col?.attr)
      const options = enumTarget ? enumOptionsCache[enumTarget] || [] : []
      if (options.length > 0) {
        return (
          <FormControl size="small" fullWidth>
            <Select
              value={def.value}
              displayEmpty
              onChange={(e) =>
                handleDefaultPatch(index, {
                  attr: def.attr,
                  kind: 'enum',
                  value: String(e.target.value),
                })
              }
              data-testid={`default-enum-value-${index}`}
            >
              <MenuItem value="">
                <em>— select —</em>
              </MenuItem>
              {options.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      }
      return (
        <TextField
          size="small"
          label="Enum value"
          value={def.value}
          onChange={(e) =>
            handleDefaultPatch(index, {
              attr: def.attr,
              kind: 'enum',
              value: e.target.value,
            })
          }
          fullWidth
          data-testid={`default-enum-value-${index}`}
        />
      )
    }

    if (def.kind === 'ref') {
      const refTarget = refTargetOf(col?.attr)
      const lookupFields = refTarget ? lookupFieldsCache[refTarget] || [] : []
      return (
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={def.lookupField}
              onChange={(e) =>
                handleDefaultPatch(index, {
                  ...def,
                  lookupField: String(e.target.value),
                })
              }
              disabled={lookupFields.length === 0}
              displayEmpty
            >
              {(lookupFields.length > 0 ? lookupFields : [def.lookupField || 'name']).map(
                (field) => (
                  <MenuItem key={field} value={field}>
                    {field}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Lookup text"
            value={def.text}
            onChange={(e) =>
              handleDefaultPatch(index, {
                ...def,
                text: e.target.value,
              })
            }
            fullWidth
          />
        </Box>
      )
    }

    // composite
    const types = compositeTypesOf(col?.attr)
    return (
      <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={def.type}
            displayEmpty
            onChange={(e) =>
              handleDefaultPatch(index, {
                ...def,
                type: String(e.target.value),
              })
            }
          >
            <MenuItem value="">
              <em>— type —</em>
            </MenuItem>
            {types.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Value"
          value={def.value ?? ''}
          onChange={(e) =>
            handleDefaultPatch(index, {
              ...def,
              value: e.target.value,
            })
          }
          fullWidth
        />
      </Box>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: RESIZABLE_DIALOG_PAPER }}
    >
      <DialogTitle>Import tabular data — {objectName}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 0: Upload */}
        {step === 0 && (
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
            onClick={() => document.getElementById('tabular-import-file-input')?.click()}
          >
            <input
              id="tabular-import-file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              hidden
              aria-label="Upload file"
              onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
            />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Click to select a CSV or Excel file
            </Typography>
            <Typography variant="caption" color="text.disabled">
              .csv, .xlsx, .xls
            </Typography>
            {loading && <CircularProgress size={24} sx={{ mt: 2 }} />}
          </Box>
        )}

        {/* Step 1: Map columns */}
        {step === 1 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {totalRows} rows found. Map file columns to target fields:
            </Typography>

            {totalRows > fileRows.length && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Showing first {fileRows.length} rows. Total: {totalRows} rows.
              </Alert>
            )}

            <TableContainer sx={{ maxHeight: 400, mb: 3 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>File column</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sample</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 200 }}>Map to</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 150 }}>Lookup by</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fileColumns.map((col) => {
                    const target = mapping[col] || { kind: 'skip' }
                    const isRef = target.kind === 'ref'
                    const refColumn =
                      isRef && target.kind === 'ref'
                        ? columns.find((c) => c.name === target.attr)
                        : null
                    const refColumnTarget = refTargetOf(refColumn?.attr)
                    const lookupFields = refColumnTarget
                      ? lookupFieldsCache[refColumnTarget] || []
                      : []

                    return (
                      <TableRow key={col}>
                        <TableCell>{col}</TableCell>
                        <TableCell
                          sx={{
                            color: 'text.secondary',
                            maxWidth: 150,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {String(fileRows[0]?.[col] ?? '—')}
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={
                                target.kind === 'skip'
                                  ? ''
                                  : target.kind === 'attr'
                                    ? target.name
                                    : target.attr
                              }
                              onChange={(e) => {
                                const value = e.target.value
                                if (!value) {
                                  handleMappingChange(col, { kind: 'skip' })
                                } else {
                                  const column = columns.find((c) => c.name === value)
                                  if (column && attrTypeOf(column.attr) === 'ref') {
                                    const columnTarget = refTargetOf(column.attr)
                                    if (columnTarget) {
                                      loadLookupFields(columnTarget)
                                    }
                                    handleMappingChange(col, {
                                      kind: 'ref',
                                      attr: value,
                                      lookupField: 'name',
                                    })
                                  } else {
                                    handleMappingChange(col, { kind: 'attr', name: value })
                                  }
                                }
                              }}
                              displayEmpty
                              data-testid={`column-mapping-${col}`}
                            >
                              <MenuItem value="">
                                <em>— skip —</em>
                              </MenuItem>
                              {columns.map((c) => (
                                <MenuItem key={c.name} value={c.name}>
                                  {c.title}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          {isRef && target.kind === 'ref' && (
                            <FormControl size="small" fullWidth>
                              <Select
                                value={target.lookupField}
                                onChange={(e) =>
                                  handleMappingChange(col, {
                                    ...target,
                                    lookupField: e.target.value,
                                  })
                                }
                                disabled={lookupFields.length === 0}
                              >
                                {lookupFields.map((field) => (
                                  <MenuItem key={field} value={field}>
                                    {field}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Defaults
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Fill empty cells after mapping (session only).
            </Typography>
            {defaults.map((def, index) => {
              const available = columns.filter(
                (c) => c.name === def.attr || !usedDefaultAttrs.has(c.name),
              )
              return (
                <Box
                  key={`${def.attr}-${index}`}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <Select
                      value={def.attr}
                      onChange={(e) => handleDefaultAttrChange(index, String(e.target.value))}
                      data-testid={`default-attr-${index}`}
                    >
                      {available.map((c) => (
                        <MenuItem key={c.name} value={c.name}>
                          {c.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ flex: 1, minWidth: 0 }}>{renderDefaultEditor(def, index)}</Box>
                  <IconButton
                    size="small"
                    aria-label={`Remove default ${def.attr}`}
                    onClick={() => handleRemoveDefault(index)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              )
            })}
            <Button
              size="small"
              onClick={handleAddDefault}
              disabled={!canAddDefault}
              sx={{ mb: 2 }}
            >
              Add default
            </Button>

            <Typography variant="body2" sx={{ mb: 1 }}>
              Import mode:
            </Typography>
            <RadioGroup
              row
              value={mode}
              onChange={(e) => setMode(e.target.value as 'append' | 'replace')}
              sx={{ mb: 2 }}
            >
              <FormControlLabel
                value="append"
                control={<Radio size="small" />}
                label="Append to existing rows"
              />
              <FormControlLabel
                value="replace"
                control={<Radio size="small" />}
                label="Replace all existing rows"
              />
            </RadioGroup>
          </>
        )}

        {/* Step 2: Preview */}
        {step === 2 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Preview of {processedRows.length} rows to import:
            </Typography>

            {warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {warnings.length} warning(s):
                </Typography>
                {warnings.slice(0, 5).map((warning, i) => (
                  <Typography key={i} variant="caption" display="block">
                    Row {warning.row}: {warning.message}
                  </Typography>
                ))}
                {warnings.length > 5 && (
                  <Typography variant="caption" color="text.secondary">
                    ...and {warnings.length - 5} more
                  </Typography>
                )}
              </Alert>
            )}

            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {previewColumns.map((col) => (
                      <TableCell key={col.name} sx={{ fontWeight: 600 }}>
                        {col.title}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processedRows.slice(0, 10).map((row, i) => (
                    <TableRow key={i}>
                      {previewColumns.map((col) => (
                        <TableCell key={col.name}>{String(row[col.name] ?? '')}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {processedRows.length > 10 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Showing first 10 rows
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        {step === 1 && (
          <Button variant="contained" onClick={buildPreview} disabled={loading || !canPreview}>
            {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Preview
          </Button>
        )}

        {step === 2 && (
          <Button variant="contained" onClick={handleConfirm} disabled={loading}>
            Confirm
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
